import React, { useMemo, useState } from "react";
import { getApiErrorMessage, putFinancePayroll } from "../../api";
import ErrorRetry from "../../components/ErrorRetry";
import Loading from "../../components/Loading";
import FinanceTabs from "./FinanceTabs";
import { formatMoney, parseAmount } from "./financeUtils";
import useFinanceOverview from "./useFinanceOverview";

const EMPTY_PAYROLL = [];

function draftValue(drafts, id, fallback) {
  return Object.prototype.hasOwnProperty.call(drafts, id) ? drafts[id] : fallback;
}

function payrollBonus(row) {
  const num = Number(row?.bonus);
  return Number.isFinite(num) ? num : 0;
}

function paidFallback(row) {
  if (row.paid_is_default) return String(Number(row.salary) / 2);
  return row.paid == null ? "" : String(row.paid);
}

function bonusFallback(row) {
  return String(payrollBonus(row));
}

function displayedPaid(row) {
  if (row.paid_is_default) return Number(row.salary) / 2;
  const num = Number(row.paid);
  return Number.isFinite(num) ? num : 0;
}

function savedPaidBase(row) {
  if (row.paid_is_default) return Number(row.salary) / 2;
  const num = Number(row.paid);
  return Number.isFinite(num) ? num : 0;
}

function formatBankPaymentStamp(paidAt) {
  const match = String(paidAt || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(paidAt || "");
  return `${match[3]}.${match[2]}`;
}

function bankPaymentsTitle(row) {
  const payments = row.bank_payments || [];
  if (!payments.length) return undefined;
  const list = payments
    .map((item) => `${formatBankPaymentStamp(item.paid_at)} · ${item.amount} ₽`)
    .join(", ");
  return `Из банка: ${list}`;
}

export default function FinancePayroll() {
  const { month, setMonth, overview, loading, error, reload } = useFinanceOverview();
  const [payrollDrafts, setPayrollDrafts] = useState({});
  const [bonusDrafts, setBonusDrafts] = useState({});
  const [addPaymentDrafts, setAddPaymentDrafts] = useState({});
  const [addingEmployeeId, setAddingEmployeeId] = useState(null);
  const [saveNotice, setSaveNotice] = useState("");
  const [actionError, setActionError] = useState("");

  const rows = overview?.payroll || EMPTY_PAYROLL;
  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => ({
        salary: acc.salary + (Number(row.salary) || 0),
        paid: acc.paid + displayedPaid(row),
        due: acc.due + (Number(row.due) || 0),
      }),
      { salary: 0, paid: 0, due: 0 }
    );
  }, [rows]);

  const resetNotices = () => {
    setSaveNotice("");
    setActionError("");
  };

  const afterSave = async () => {
    setSaveNotice("Сохранено");
    await reload();
  };

  const failSave = (err) => {
    setSaveNotice("");
    setActionError(getApiErrorMessage(err, "Не удалось сохранить"));
  };

  const collectPayrollItems = () => {
    const items = [];
    for (const row of rows) {
      const paidParsed = parseAmount(draftValue(payrollDrafts, row.employee_id, paidFallback(row)));
      const bonusParsed = parseAmount(draftValue(bonusDrafts, row.employee_id, bonusFallback(row)));
      if (!paidParsed.ok || !bonusParsed.ok) return null;
      items.push({
        employee_id: row.employee_id,
        amount: paidParsed.value,
        bonus: bonusParsed.value,
      });
    }
    return items;
  };

  const handlePayrollBlur = async () => {
    const items = collectPayrollItems();
    if (!items) return;
    const unchanged = items.every((item) => {
      const row = rows.find((entry) => entry.employee_id === item.employee_id);
      return displayedPaid(row) === item.amount && payrollBonus(row) === item.bonus;
    });
    if (unchanged) return;
    resetNotices();
    try {
      await putFinancePayroll({ month, items });
      await afterSave();
    } catch (err) {
      failSave(err);
    }
  };

  const handleAddPayment = async (row) => {
    const parsed = parseAmount(draftValue(addPaymentDrafts, row.employee_id, ""));
    if (!parsed.ok || parsed.value <= 0) {
      setSaveNotice("");
      setActionError("Не удалось сохранить");
      return;
    }
    const amount = savedPaidBase(row) + parsed.value;
    resetNotices();
    setAddingEmployeeId(row.employee_id);
    try {
      await putFinancePayroll({
        month,
        items: [{
          employee_id: row.employee_id,
          amount,
          bonus: payrollBonus(row),
        }],
      });
      setAddPaymentDrafts((prev) => {
        const next = { ...prev };
        delete next[row.employee_id];
        return next;
      });
      await afterSave();
    } catch (err) {
      failSave(err);
    } finally {
      setAddingEmployeeId(null);
    }
  };

  return (
    <div className="page-container fin-page">
      <FinanceTabs month={month} onMonthChange={setMonth} title="Зарплата" />
      {error && <ErrorRetry message={error} onRetry={() => reload()} />}
      {saveNotice && (
        <div className="fin-save-ok" role="status">{saveNotice}</div>
      )}
      {actionError && (
        <div className="login-error" role="status" aria-live="polite">{actionError}</div>
      )}
      {loading && !overview ? (
        <Loading text="Загрузка кассы…" />
      ) : overview ? (
        <section className="fin-section" aria-label="Зарплата">
          <div className="fin-totals">
            <span>Оклады: {formatMoney(totals.salary)}</span>
            <span>Выплачено: {formatMoney(totals.paid)}</span>
            <span>К выдаче: {formatMoney(totals.due)}</span>
          </div>
          <div className="fin-panel">
            <div className="fin-table-wrap">
              <table className="fin-payroll">
            <caption>Зарплата</caption>
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th>Оклад</th>
                <th>Премия</th>
                <th>Выплачено</th>
                <th>К выдаче</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.employee_id}>
                  <td>{row.name}</td>
                  <td>{formatMoney(row.salary)}</td>
                  <td>
                    <label className="login-label">
                      Премия {row.name}
                      <input
                        className="login-input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={draftValue(bonusDrafts, row.employee_id, bonusFallback(row))}
                        onChange={(e) => {
                          setBonusDrafts((prev) => ({
                            ...prev,
                            [row.employee_id]: e.target.value,
                          }));
                        }}
                        onBlur={handlePayrollBlur}
                      />
                    </label>
                  </td>
                  <td title={bankPaymentsTitle(row)}>
                    <label className="login-label">
                      Выплачено {row.name}
                      <input
                        className="login-input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={draftValue(payrollDrafts, row.employee_id, paidFallback(row))}
                        onChange={(e) => {
                          setPayrollDrafts((prev) => ({
                            ...prev,
                            [row.employee_id]: e.target.value,
                          }));
                        }}
                        onBlur={handlePayrollBlur}
                      />
                    </label>
                    <input
                      className="login-input"
                      type="number"
                      step="0.01"
                      min="0"
                      aria-label={`Сумма выплаты ${row.name}`}
                      value={draftValue(addPaymentDrafts, row.employee_id, "")}
                      onChange={(e) => {
                        setAddPaymentDrafts((prev) => ({
                          ...prev,
                          [row.employee_id]: e.target.value,
                        }));
                      }}
                    />
                    <button
                      type="button"
                      aria-label={`Добавить выплату ${row.name}`}
                      disabled={addingEmployeeId === row.employee_id}
                      onClick={() => handleAddPayment(row)}
                    >
                      Добавить
                    </button>
                  </td>
                  <td className={Number(row.due) > 0 ? "fin-due" : undefined}>
                    {formatMoney(row.due)}
                  </td>
                </tr>
              ))}
            </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}
